import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Single-letter mono lead-in tag used in place of a per-kind icon —
 * see roland-sxx0-editor/src/plugins/shared/item-types.tsx for the
 * canonical comment. Items here are common-area (S = sample, K = program-kit).
 */
function KindTag({ children }) {
    return _jsx("span", { className: "ac-tree-item-tag", "aria-hidden": "true", children: children });
}
// =========================================================================
// Sample item type factory
// =========================================================================
/**
 * Create an item type plugin for common-area samples.
 *
 * @param supportedActions - Transfer actions this editor can handle.
 *   Only actions in this set appear in the context menu.
 */
export function createCommonSampleItemType(supportedActions) {
    return {
        typeId: 'sample',
        displayName: 'Sample',
        renderIcon: () => _jsx(KindTag, { children: "S" }),
        renderTrailing: (meta) => {
            if (!meta.sliceCount || meta.sliceCount <= 0)
                return null;
            const unit = meta.hasDrumKit ? 'pad' : 'slice';
            return (_jsxs("span", { className: "text-xs text-gray-400", children: [meta.sliceCount, " ", unit, meta.sliceCount !== 1 ? 's' : ''] }));
        },
        isDraggable: () => true,
        supportsRename: true,
        getContextMenuActions: (meta) => {
            const actions = [];
            // Transfer actions — batchable (send multiple samples to device)
            if (supportedActions.has('send-sample-to-device')) {
                actions.push({ id: 'send-sample-to-device', label: 'Send to Device', icon: null, batchable: false });
            }
            if (meta.hasDrumKit && supportedActions.has('import-drum-kit')) {
                actions.push({ id: 'import-drum-kit', label: 'Import as Drum Program', icon: null, batchable: false });
            }
            // Editor actions — single-item only (one editor window at a time)
            if (actions.length > 0)
                actions.push({ separator: true });
            actions.push({ id: 'open-loop-editor', label: 'Open in Loop Editor', icon: null, batchable: false });
            actions.push({ id: 'open-sample-editor', label: 'Open in Sample Editor', icon: null, batchable: false });
            actions.push({ id: 'open-chopper', label: 'Open in Chopper', icon: null, batchable: false });
            if (meta.hasDrumKit && supportedActions.has('edit-drum-kit')) {
                actions.push({ id: 'edit-drum-kit', label: 'Edit Kit', icon: null, batchable: false });
            }
            // File operations
            actions.push({ separator: true });
            actions.push({ id: 'rename', label: 'Rename', icon: null, batchable: false });
            actions.push({ id: 'move', label: 'Move to...', icon: null, batchable: true });
            actions.push({ separator: true });
            actions.push({ id: 'delete', label: 'Delete', icon: null, danger: true, batchable: true });
            return actions;
        },
    };
}
// =========================================================================
// Program item type factory
// =========================================================================
/**
 * Create an item type plugin for common-area programs.
 *
 * @param supportedActions - Transfer actions this editor can handle.
 *   Only actions in this set appear in the context menu.
 */
export function createCommonProgramItemType(supportedActions) {
    return {
        typeId: 'program',
        displayName: 'Program',
        renderIcon: () => _jsx(KindTag, { children: "K" }),
        renderTrailing: (meta) => {
            if (!meta.kitCount || meta.kitCount <= 0)
                return null;
            return (_jsxs("span", { className: "text-xs text-gray-400", children: [meta.kitCount, " zone", meta.kitCount !== 1 ? 's' : ''] }));
        },
        isDraggable: () => true,
        supportsRename: true,
        getContextMenuActions: () => {
            const actions = [];
            // Transfer actions
            if (supportedActions.has('send-program-to-device')) {
                actions.push({ id: 'send-program-to-device', label: 'Send to Device', icon: null, batchable: false });
            }
            if (supportedActions.has('promote-to-common-area')) {
                actions.push({ id: 'promote-to-common-area', label: 'Promote to Common Area', icon: null, batchable: false });
            }
            if (supportedActions.has('import-instrument')) {
                actions.push({ id: 'import-instrument', label: 'Import to Device', icon: null, batchable: false });
            }
            // File operations
            if (actions.length > 0)
                actions.push({ separator: true });
            actions.push({ id: 'rename', label: 'Rename', icon: null, batchable: false });
            actions.push({ id: 'move', label: 'Move to...', icon: null, batchable: true });
            actions.push({ separator: true });
            actions.push({ id: 'delete', label: 'Delete', icon: null, danger: true, batchable: true });
            return actions;
        },
    };
}
//# sourceMappingURL=item-types.js.map